import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import GameShell from '../../shared/ui/GameShell';
import LoadingScreen from '../../shared/ui/LoadingScreen';
import ErrorScreen from '../../shared/ui/ErrorScreen';
import FinishedDaily from '../../shared/ui/FinishedDaily';
import { finish as recordOutcome, isFinished } from '../../shared/dailySession';
import { useDailySnapshot, usePersistedState } from '../../shared/useDailySession';
import DailyDoneOverlay from '../../shared/ui/DailyDoneOverlay';
import { loadTodaysPuzzle, formatDateLabel } from '../../shared/daily';
import {
  ACTIVE_POSITIONS,
  completedWords,
  evaluateTiles,
  findBestSwap,
  parseInitialLetters,
  solvedLetters,
  WORD_INDICES,
  type HashtagLevel,
  type TileState,
} from './types';
import type { GauntletAware } from '../../shared/gauntletStep';
import './hashtag.css';

const MAX_MOVES = 12;

export default function HashtagGame() {
  const [data, setData] = useState<{
    level: HashtagLevel;
    dateKey: string;
    isToday: boolean;
  } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadTodaysPuzzle<HashtagLevel>('hashtag')
      .then((res) => {
        if (cancelled) return;
        if (!res) setLoadError('No daily puzzle published for Hashtag yet.');
        else setData({ level: res.data, dateKey: res.dateKey, isToday: res.isToday });
      })
      .catch((e) => !cancelled && setLoadError(String(e?.message ?? e)));
    return () => {
      cancelled = true;
    };
  }, []);

  if (loadError) return <ErrorScreen message={loadError} gameId="hashtag" />;
  if (!data) {
    return (
      <GameShell gameId="hashtag">
        <LoadingScreen label="Fetching today's puzzle…" />
      </GameShell>
    );
  }

  // A daily is one attempt. Once the round has resolved, show the result
  // instead of mounting a fresh board.
  if (isFinished('hashtag', data.dateKey)) {
    return (
      <FinishedDaily gameId="hashtag" dateKey={data.dateKey} isToday={data.isToday} />
    );
  }

  return (
    <HashtagPlay
      key={data.dateKey}
      level={data.level}
      dateKey={data.dateKey}
      isToday={data.isToday}
    />
  );
}

interface DragState {
  idx: number;
  startX: number;
  startY: number;
  curX: number;
  curY: number;
  moved: boolean;
}

export function HashtagPlay({
  level,
  dateKey,
  isToday,
  gauntlet,
}: GauntletAware & {
  level: HashtagLevel;
  dateKey: string;
  isToday: boolean;
}) {
  const solution = useMemo(() => level.words.map((w) => w.toUpperCase()), [level]);
  const initialLetters = useMemo(() => parseInitialLetters(level.initialLetters), [level]);

  const { restored, save } = useDailySnapshot<{
    tiles: string[];
    movesLeft: number;
    hintUsed: boolean;
  }>('hashtag', dateKey);

  const [tiles, setTiles] = useState<string[]>(() => restored?.tiles ?? initialLetters);
  const [selected, setSelected] = useState<number | null>(null);
  const [movesLeft, setMovesLeft] = useState(restored?.movesLeft ?? MAX_MOVES);
  const [gameState, setGameState] = useState<'playing' | 'won' | 'lost'>('playing');
  const [drag, setDrag] = useState<DragState | null>(null);
  const [swapPulse, setSwapPulse] = useState<Set<number>>(new Set());
  const [hintIndices, setHintIndices] = useState<Set<number>>(new Set());
  const [hintUsed, setHintUsed] = useState(restored?.hintUsed ?? false);

  usePersistedState(
    save,
    { tiles, movesLeft, hintUsed },
    !gauntlet && gameState === 'playing',
  );

  const states = useMemo(() => evaluateTiles(tiles, solution), [tiles, solution]);
  const completedSet = useMemo(() => completedWords(tiles, solution), [tiles, solution]);

  // Guarded by a ref: the host may hand a fresh `gauntlet` object each render.
  const solvedRef = useRef(false);
  useEffect(() => {
    if (gameState !== 'won' || solvedRef.current) return;
    solvedRef.current = true;
    if (gauntlet) gauntlet.onSolved();
    else
      recordOutcome('hashtag', dateKey, 'won', {
        subtitle: `Used ${MAX_MOVES - movesLeft} of ${MAX_MOVES} moves.`,
      });
  }, [gameState, dateKey, gauntlet, movesLeft]);

  // Running out of moves ends the day in daily mode; the gauntlet host
  // handles its own life accounting.
  const failedRef = useRef(false);
  useEffect(() => {
    if (gauntlet || gameState !== 'lost' || failedRef.current) return;
    failedRef.current = true;
    recordOutcome('hashtag', dateKey, 'lost', {
      subtitle: 'Ran out of moves.',
      answer: solution.join('  '),
    });
  }, [gameState, dateKey, gauntlet, solution]);

  const triggerPulse = useCallback((a: number, b: number) => {
    setSwapPulse(new Set([a, b]));
    setTimeout(() => setSwapPulse(new Set()), 300);
  }, []);

  const doSwap = useCallback(
    (from: number, to: number) => {
      if (movesLeft <= 0 || gameState !== 'playing' || from === to) return;
      setTiles((prev) => {
        const next = [...prev];
        [next[from], next[to]] = [next[to], next[from]];
        return next;
      });
      setMovesLeft((m) => m - 1);
      setHintIndices(new Set());
      triggerPulse(from, to);
    },
    [movesLeft, gameState, triggerPulse],
  );

  const useHint = useCallback(() => {
    // The host gates hint availability in the gauntlet, so the local
    // one-per-puzzle limit doesn't apply there.
    if (gameState !== 'playing' || (hintUsed && !gauntlet)) return;
    const target = solvedLetters(solution);
    const swap = findBestSwap(tiles, target);
    if (!swap) return;
    setSelected(null);
    setHintUsed(true);
    setHintIndices(new Set([swap.from, swap.to]));
  }, [gameState, hintUsed, tiles, solution, gauntlet]);

  useEffect(() => {
    if (gameState !== 'playing') return;
    if (completedSet.size === 4) setGameState('won');
    else if (movesLeft === 0) setGameState('lost');
  }, [completedSet.size, movesLeft, gameState]);

  // Gauntlet: exhausting the move budget costs a life, then the board resets so
  // the player can keep going — the host decides when the run is over.
  const lostRef = useRef(false);
  useEffect(() => {
    if (!gauntlet || gameState !== 'lost' || lostRef.current) return;
    lostRef.current = true;
    gauntlet.onWrong();
  }, [gauntlet, gameState]);

  const selectTile = useCallback(
    (index: number) => {
      if (gameState !== 'playing') return;
      setHintIndices(new Set());
      if (selected === null) {
        setSelected(index);
      } else if (selected === index) {
        setSelected(null);
      } else {
        doSwap(selected, index);
        setSelected(null);
      }
    },
    [selected, gameState, doSwap],
  );

  const reset = useCallback(() => {
    setTiles(initialLetters);
    setSelected(null);
    setMovesLeft(MAX_MOVES);
    setHintIndices(new Set());
    setHintUsed(false);
    setGameState('playing');
  }, [initialLetters]);

  // Each increment of the host's hint signal spends one pooled hint here.
  const lastHintSignal = useRef(gauntlet?.hintSignal ?? 0);
  useEffect(() => {
    const signal = gauntlet?.hintSignal ?? 0;
    if (signal > lastHintSignal.current) {
      lastHintSignal.current = signal;
      useHint();
    }
  }, [gauntlet?.hintSignal, useHint]);

  const actions = (
    <>
      <button
        className="btn btn-ghost ht-small-btn"
        onClick={useHint}
        disabled={gameState !== 'playing' || hintUsed}
        title="Highlight a useful swap (one per puzzle)"
      >
        💡 Hint
      </button>
      <button className="btn btn-secondary ht-small-btn" onClick={reset}>
        Reset
      </button>
    </>
  );

  const body = (
    <>
      <div className="ht-status">
        <div
          className={
            'ht-chip ' +
            (movesLeft <= 1 ? 'is-danger' : movesLeft <= 3 ? 'is-warn' : 'is-ok')
          }
        >
          Moves left: <strong>{movesLeft}</strong>
        </div>
      </div>

      <HashtagGrid
        tiles={tiles}
        states={states}
        selected={selected}
        completedSet={completedSet}
        onSelect={selectTile}
        onSwap={doSwap}
        disabled={gameState !== 'playing'}
        drag={drag}
        setDrag={setDrag}
        swapPulse={swapPulse}
        hintIndices={hintIndices}
      />

      <div className="ht-legend">
        <span className="ht-legend-item">
          <span className="ht-legend-box is-correct" /> Correct
        </span>
        <span className="ht-legend-item">
          <span className="ht-legend-box is-wrong" /> Wrong spot
        </span>
      </div>

      {gameState === 'won' && !gauntlet && (
        <DailyDoneOverlay
          dateKey={dateKey}
          isToday={isToday}
          subtitle={`Used ${MAX_MOVES - movesLeft} / ${MAX_MOVES} moves.`}
        />
      )}

      {gameState === 'lost' && (
        <div className="ht-overlay">
          <div className="ht-overlay-card">
            <div className="ht-overlay-emoji">😬</div>
            <div className="ht-overlay-title">Out of moves</div>
            <div className="ht-overlay-sub">
              {gauntlet
                ? 'That cost you a life. Reset and keep going.'
                : 'Try again with a fresh board.'}
            </div>
            <button
              className="btn btn-primary"
              onClick={() => {
                lostRef.current = false;
                reset();
              }}
            >
              Try again
            </button>
          </div>
        </div>
      )}
    </>
  );

  // In the gauntlet the host supplies the chrome, so render bare.
  if (gauntlet) return <div className="gauntlet-embed">{body}</div>;

  return (
    <GameShell
      gameId="hashtag"
      actions={actions}
      subtitle={`${formatDateLabel(dateKey, isToday)} • ${completedSet.size}/4 words`}
    >
      {body}
    </GameShell>
  );
}

function HashtagGrid({
  tiles,
  states,
  selected,
  completedSet,
  onSelect,
  onSwap,
  disabled,
  drag,
  setDrag,
  swapPulse,
  hintIndices,
}: {
  tiles: string[];
  states: TileState[];
  selected: number | null;
  completedSet: Set<number>;
  onSelect: (i: number) => void;
  onSwap: (from: number, to: number) => void;
  disabled: boolean;
  drag: DragState | null;
  setDrag: (d: DragState | null) => void;
  swapPulse: Set<number>;
  hintIndices: Set<number>;
}) {
  const gridRef = useRef<HTMLDivElement>(null);

  const wordMembership = (tileIdx: number): number[] => {
    const memberships: number[] = [];
    for (let w = 0; w < 4; w++) {
      if (WORD_INDICES[w].includes(tileIdx)) memberships.push(w);
    }
    return memberships;
  };

  // Resolve which tile index the pointer is currently over.
  const tileAtPoint = useCallback((clientX: number, clientY: number): number | null => {
    const el = document.elementFromPoint(clientX, clientY);
    if (!el) return null;
    const tile = (el as Element).closest('.ht-tile') as HTMLElement | null;
    if (!tile) return null;
    const idx = tile.dataset.idx;
    return idx != null ? parseInt(idx, 10) : null;
  }, []);

  const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>, idx: number) => {
    if (disabled) return;
    (e.currentTarget as HTMLButtonElement).setPointerCapture(e.pointerId);
    setDrag({
      idx,
      startX: e.clientX,
      startY: e.clientY,
      curX: e.clientX,
      curY: e.clientY,
      moved: false,
    });
  };

  const onPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!drag) return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    const moved = drag.moved || Math.hypot(dx, dy) > 6;
    setDrag({ ...drag, curX: e.clientX, curY: e.clientY, moved });
  };

  const onPointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!drag) return;
    try {
      (e.currentTarget as HTMLButtonElement).releasePointerCapture(e.pointerId);
    } catch {}

    if (!drag.moved) {
      // No drag — treat as a click-to-select.
      onSelect(drag.idx);
    } else {
      const targetIdx = tileAtPoint(e.clientX, e.clientY);
      if (targetIdx != null && targetIdx !== drag.idx) {
        onSwap(drag.idx, targetIdx);
      }
    }
    setDrag(null);
  };

  return (
    <div className="ht-grid-wrap">
      <div className="ht-grid" ref={gridRef}>
        {Array.from({ length: 5 }, (_, row) =>
          Array.from({ length: 5 }, (_, col) => {
            const idx = ACTIVE_POSITIONS.findIndex((p) => p.row === row && p.col === col);
            if (idx === -1) return <div key={`${row}-${col}`} className="ht-empty" />;
            const state = states[idx];
            const isWordDone = wordMembership(idx).some((w) => completedSet.has(w));
            const isDragging = drag?.idx === idx && drag.moved;
            const isPulsing = swapPulse.has(idx);
            const isHinted = hintIndices.has(idx);
            const style: React.CSSProperties | undefined = isDragging
              ? {
                  transform: `translate(${drag.curX - drag.startX}px, ${
                    drag.curY - drag.startY
                  }px) scale(1.1)`,
                  zIndex: 10,
                  transition: 'none',
                  cursor: 'grabbing',
                }
              : undefined;
            return (
              <button
                key={`${row}-${col}`}
                data-idx={idx}
                className={
                  'ht-tile' +
                  ` is-${state}` +
                  (selected === idx ? ' is-selected' : '') +
                  (isWordDone ? ' is-word-done' : '') +
                  (isDragging ? ' is-dragging' : '') +
                  (isPulsing ? ' is-swap-pulse' : '') +
                  (isHinted ? ' is-hinted' : '')
                }
                style={style}
                onPointerDown={(e) => onPointerDown(e, idx)}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={() => setDrag(null)}
                disabled={disabled}
              >
                {tiles[idx]}
              </button>
            );
          }),
        )}
      </div>
    </div>
  );
}
